<template>
  <CreateModal @closeCreateModal="resetForm" ref="createModal" type="host">
    <v-form class="mw-700 py-5 mx-auto" ref="hostForm" v-model="valid">
      <h3 class="mb-3">{{ $t('language.location') }}</h3>

      <v-row >
        <v-col cols="12" sm="4" class="pb-0">
          <HCountrySelect
            v-model="host.country"
            :country="host.country"
          />
        </v-col>
        <v-col cols="6" sm="5" class="pb-0">
          <HLanguageSelect
            v-model="host.language"
            :country="host.country"
          />
        </v-col>
        <v-col cols="6" sm="3" class="pb-0">
          <HCurrencySelect
            v-model="host.currency"
            :country="host.country"
            :disabled="true"
          />
        </v-col>
        <v-col cols="12"  class="pb-0 mt-n8">
          <HCitySelect v-model="host.city_id" :country="country" />
        </v-col>
      </v-row>

      <v-divider class="mb-10" />

      <h3 class="mb-3">{{ $t('host.titles.hostInfo') }}</h3>

      <v-row>
        <v-col cols="10">
          <v-text-field
            v-model="host.name"
            :label="`${$t('host.label.name')}*`"
            :rules="required"
            outlined
          />
        </v-col>
        <v-col cols="1" class="mt-2">
          <HostFavorite
            :starred="starred"
            @setStarred="setStarred"
          />
        </v-col>
        <v-col cols="1" class="mt-2">
          <HostMonitored
            :monitored="monitored"
            @setMonitored="setMonitored"
          />
        </v-col>
      </v-row>

      <v-text-field
        v-model="host.username"
        :label="`${$t('host.label.username')}*`"
        :rules="emailRule"
        autocomplete="new-email"
        outlined
      />

      <HPhone
        v-model="host.phone"
        :rules="required"
        :country="country"
      />

      <HDocumentID
        v-model="host.cpf"
        :rules="cpfRule"
        :country="country"
        :label="$t('host.label.cpf')"
      />

      <v-text-field
        v-if="country === 'BR'"
        v-model="host.rg"
        :label="$t('host.label.rg')"
        :rules="rgRule"
        outlined
      />

      <v-text-field
        v-model="host.birth_date"
        :label="$t('host.label.birthDate')"
        :hint="$t('forms.dateHint')"
        :rules="[rules.birthDateCheck]"
        v-mask="'##/##/####'"
        maxlength="10"
        class="mw-200"
        outlined
      />

      <v-divider class="mb-5" />

      <v-switch
        :label="$t('host.label.generatePassword')"
        v-model="host.generate_password"
        @change="resetPassword()"
        inset
      />

      <HTextInput
        v-if="!host.generate_password"
        v-model="host.password"
        :label="`${$t('host.label.password')}*`"
        :rules="passwordRule"
        autocomplete="new-password"
        type="password"
      />

      <v-divider class="mb-8" />

      <v-switch :label="$t('host.label.tester')" v-model="host.tester" inset />

      <v-switch
        :label="$t('host.label.hasPlatformAccess')"
        v-model="host.has_plataform_access"
        inset
      />

      <v-divider class="mt-3 mb-8" />

      <HPartnerSelect v-model="partner" />

      <v-divider class="mt-1 mb-8" />

      <HServiceProviderSelect v-model="serviceProvider" />

      <v-divider class="mt-1 mb-8" />

      <HostServiceTypeSelect v-model="host.service_type" />

      <v-divider class="mt-1 mb-8" />
      <h3 class="mb-3">{{ $t('host.titles.billingData')}}</h3>

      <v-row>
        <v-col cols="12">
          <v-text-field
            v-model="host.invoice_company_name"
            :label="$t('host.label.customerName')"
            outlined
            clearable
          />
        </v-col>

        <v-col cols="12">
          <v-text-field
            v-model="host.cnpj"
            :label="$t('host.label.customerCnpj')"
            :rules="[cnpjOptionalRule]"
            v-mask="'##.###.###/####-##'"
            maxlength="18"
            outlined
            clearable
          />
        </v-col>

        <v-col cols="12">
          <v-autocomplete
            v-model="host.tax_regime"
            :items="taxRegimeItems"
            :label="$t('host.label.taxRegime')"
            item-text="text"
            item-value="value"
            clearable
            outlined
          />
        </v-col>
      </v-row>

      <div class="d-flex justify-end mt-5">
        <v-btn
          elevation="0"
          color="primary"
          @click="create"
          :loading="isLoading"
          :disabled="!valid || !hasServiceTypeSelected"
          large
        >
          {{ $t('create.save') }}
        </v-btn>
      </div>
    </v-form>
  </CreateModal>
</template>
<script>
import CreateModal from '@/components/app/CreateModal'
import BirthDatePicker from '@/components/app/BirthDatePicker'
import HAutocomplete from '@/components/app/HAutocomplete'
import HTextInput from '@/components/app/HTextInput'
import HCheckbox from '@/components/app/HCheckbox'
import HostServiceTypeSelect from '@/components/host/HostServiceTypeSelect'
import HostFavorite from './components/HostFavorite'
import HostMonitored from './components/HostMonitored'
import HDocumentID from '../app/HDocumentID'
import HPhone from '../app/HPhone'
import HCountrySelect from '../app/HCountrySelect'
import HCurrencySelect from '../app/HCurrencySelect'
import HLanguageSelect from '../app/HLanguageSelect'
import HCitySelect from '../app/HCitySelect'
import HPartnerSelect from '../app/HPartnerSelect'
import HServiceProviderSelect from '../app/HServiceProviderSelect'

import { mapActions, mapGetters, mapMutations } from 'vuex'
import { birthDateCheck } from '@/util/errorHandling'
import {
  required,
  emailRule,
  passwordRule,
  cpfRule,
  rgRule,
  phoneRule,
} from '@/util/defaultValidationRules'
import { unmaskText } from '@/util/helpers'

export default {
  name: 'HostCreate',

  components: {
    CreateModal,
    HAutocomplete,
    HTextInput,
    HCheckbox,
    HostServiceTypeSelect,
    BirthDatePicker,
    HostFavorite,
    HostMonitored,
    HDocumentID,
    HPhone,
    HCountrySelect,
    HCurrencySelect,
    HLanguageSelect,
    HCitySelect,
    HPartnerSelect,
    HServiceProviderSelect,
  },

  data() {
    return {
      host: {
        service_type: [],
        tester: false,
        birth_date: null,
        generate_password: true,
        has_plataform_access: false,
        currency: 'BRAZILIAN_REAL',
        country: 'BR',
        language: 'PT_BR',
        invoice_company_name: null,
        cnpj: null,
        tax_regime: null,
      },

      valid: false,
      starred: false,
      monitored: false,
      partner: null,
      serviceProvider: null,

      rules: {
        birthDateCheck
      },

      taxRegimes: ['MEI', 'SIMPLES_NACIONAL', 'LUCRO_PRESUMIDO', 'LUCRO_REAL'],
    }
  },

  computed: {
    ...mapGetters(['cities', 'isLoading', 'createHostSuccess','partners']),

    hasServiceTypeSelected() {
      return this.host.service_type.length > 0
    },

    required() {
      return required
    },

    emailRule() {
      return this.required.concat(emailRule)
    },

    passwordRule() {
      return this.host.generate_password ? passwordRule : this.required.concat(passwordRule)
    },

    cpfRule() {
      return cpfRule
    },

    rgRule() {
      return rgRule
    },

    phoneRule() {
      return this.required.concat(phoneRule)
    },

    country() {
      return this.host.country
    },

    taxRegimeItems() {
      return this.taxRegimes.map((value) => ({
        value,
        text: this.$t(`host.taxRegime.${value}`) || value.replace('_', ' '),
      }))
    },
  },

  methods: {
    ...mapActions([
      'fetchEnabledCitiesAdmin',
      'createHost',
      'showSuccessMessage',
      'showErrorMessage',
      'fetchHosts',
      'fetchPartners',
    ]),

    ...mapMutations(['setIsLoading']),

    async create() {
      this.setIsLoading(true)

      const body = {
        birth_date: this.parseBirthDate(this.host.birth_date),
        city_id: this.host.city_id,
        cpf: unmaskText(this.host.cpf),
        rg: this.host.rg,
        name: this.host.name,
        password: this.host.password,
        phone: this.host.phone,
        service_type: this.host.service_type,
        tester: this.host.tester,
        username: this.host.username,
        starred: this.starred,
        monitored: this.monitored,
        generate_password: this.host.generate_password,
        has_plataform_access: this.host.has_plataform_access,
        country: this.host.country,
        currency: this.host.currency,
        language: this.host.language,
        partner_id: this.partner?.id ?? null,
        service_provider_id: this.serviceProvider?.id ?? null,
        is_monitored: this.monitored,
        invoice_company_name: this.host.invoice_company_name,
        cnpj: unmaskText(this.host.cnpj),
        tax_regime: this.host.tax_regime,
      }

      this.createHost(body)
        .then(() => {
          this.showSuccessMessage('Host criado com sucesso!')
          this.fetchHosts()
          this.resetForm()
        })
        .then(() => this.$refs.createModal.closeCreateModal())
        .catch(({ data }) => {
          if (data.errors) {
            let errorMessage = ''
            data.errors.forEach((error) => {
              errorMessage += `${this.$t('host.label.' + error.field)}: ${
                error.message
              }; `
            })
            this.showErrorMessage(errorMessage)
          } else {
            this.showErrorMessage(data.message)
          }
        })
        .finally(() => {
          this.setIsLoading(false)
        })
    },

    parseBirthDate(date) {
      const splittedDate = date.split('/')
      const parsedDate = `${splittedDate[2]}-${splittedDate[1]}-${splittedDate[0]}`
      return parsedDate + 'T12:00:00-03:00'
    },

    resetForm() {
      this.$refs?.hostForm?.reset()
      this.partner = null
      this.serviceProvider = null
      this.starred = false
      this.monitored = false
      this.valid = false
      this.host = {
        service_type: [],
        birth_date: null,
        tester: false,
        name: null,
        username: null,
        phone: null,
        password: null,
        city_id: null,
        cpf: null,
        rg: null,
        generate_password: true,
        has_plataform_access: false,
        country: 'BR',
        currency: 'BRAZILIAN_REAL',
        language: 'PT_BR',
        partner_id: null,
        invoice_company_name: null,
        cnpj: null,
        tax_regime: null,
      }
    },

    resetPassword() {
      this.host.password = null
    },

    setStarred(status) {
      this.starred = status
    },
    setMonitored(status) {
      this.monitored = status
    },
  },
}
</script>
